import type { BrokerAccountRef } from "../../../utils/brokerAccountRef";
import {
  brokerAccountRefKey,
  resolveEntityBrokerAccounts,
} from "../../../utils/brokerAccountRef";
import type {
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodePosition,
} from "../../../types/workspace";
import {
  WORKSPACE_GRAPH_BRANCH_GAP,
  WORKSPACE_GRAPH_COLUMN_GAP,
  WORKSPACE_GRAPH_ORIGIN_X,
  WORKSPACE_GRAPH_ROW_GAP,
  WORKSPACE_PLACEHOLDER_COLUMN_GAP,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
  getWorkspaceNodeDimensions,
} from "./workspaceGraphConstants";
import { layoutWorkspaceGraphTree } from "./workspaceDagreLayout";
import type { WorkspaceLayoutMode } from "./workspaceLayoutMode";

export type ExecutingAccountBridge = {
  fromPolicyNodeId: string;
  toAccountNodeId: string;
  account: BrokerAccountRef;
};

function buildChildrenMap(edges: WorkspaceEdge[]): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    const siblings = childrenByParent.get(edge.sourceNodeId) ?? [];
    if (!siblings.includes(edge.targetNodeId)) siblings.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, siblings);
  }
  return childrenByParent;
}

function buildParentMap(edges: WorkspaceEdge[]): Map<string, string> {
  const parentByChild = new Map<string, string>();
  for (const edge of edges) {
    if (!parentByChild.has(edge.targetNodeId)) {
      parentByChild.set(edge.targetNodeId, edge.sourceNodeId);
    }
  }
  return parentByChild;
}

function collectSubtreeIds(
  rootId: string,
  childrenByParent: Map<string, string[]>
): Set<string> {
  const ids = new Set<string>([rootId]);
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (ids.has(id)) continue;
    ids.add(id);
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
  return ids;
}

function fedAccountRef(node: WorkspaceNode): BrokerAccountRef | null {
  return (
    resolveEntityBrokerAccounts({
      brokerName: node.brokerName,
      brokerAccountCode: node.brokerAccountCode ?? node.entityId,
    })[0] ?? null
  );
}

function matchFedAccount(
  accounts: WorkspaceNode[],
  ref: BrokerAccountRef
): WorkspaceNode | null {
  const key = brokerAccountRefKey(ref);
  for (const account of accounts) {
    const accountRef = fedAccountRef(account);
    if (accountRef && brokerAccountRefKey(accountRef) === key) return account;
    const code = String(account.brokerAccountCode ?? account.entityId ?? "").trim();
    if (
      code === ref.brokerAccountCode.trim() &&
      String(account.brokerName ?? "")
        .trim()
        .toLowerCase() === ref.brokerName
    ) {
      return account;
    }
  }
  return null;
}

/** Policy/combined_policy → fed-account bridges from `meta.accounts`. */
export function listExecutingAccountBridges(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): ExecutingAccountBridge[] {
  const accounts = nodes.filter((node) => node.type === "account");
  if (accounts.length === 0) return [];

  const bridges: ExecutingAccountBridge[] = [];
  const seen = new Set<string>();

  for (const node of nodes) {
    if (node.type !== "policy" && node.type !== "combined_policy") continue;
    const refs = resolveEntityBrokerAccounts({ accounts: node.meta?.accounts });
    for (const ref of refs) {
      const target = matchFedAccount(accounts, ref);
      if (!target) continue;
      const key = `${node.id}→${target.id}:${brokerAccountRefKey(ref)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      bridges.push({
        fromPolicyNodeId: node.id,
        toAccountNodeId: target.id,
        account: ref,
      });
    }
  }

  void edges;
  return bridges;
}

function layoutBranchGap(compact: boolean): number {
  return compact ? WORKSPACE_GRAPH_ROW_GAP : WORKSPACE_GRAPH_BRANCH_GAP;
}

function measureSubtreeExtent(
  rootId: string,
  positions: Map<string, WorkspaceNodePosition>,
  childrenByParent: Map<string, string[]>,
  nodeById: Map<string, WorkspaceNode>
): { top: number; bottom: number; left: number; right: number } | null {
  const ids = collectSubtreeIds(rootId, childrenByParent);
  let top = Infinity;
  let bottom = -Infinity;
  let left = Infinity;
  let right = -Infinity;
  let any = false;
  Array.from(ids).forEach((id) => {
    const node = nodeById.get(id);
    const pos = positions.get(id);
    if (!node || !pos) return;
    const { width, height } = getWorkspaceNodeDimensions(node);
    any = true;
    top = Math.min(top, pos.y);
    bottom = Math.max(bottom, pos.y + height);
    left = Math.min(left, pos.x);
    right = Math.max(right, pos.x + width);
  });
  if (!any) return null;
  return { top, bottom, left, right };
}

function shiftSubtree(
  rootId: string,
  dx: number,
  dy: number,
  childrenByParent: Map<string, string[]>,
  positions: Map<string, WorkspaceNodePosition>
): void {
  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
  Array.from(collectSubtreeIds(rootId, childrenByParent)).forEach((id) => {
    const pos = positions.get(id);
    if (!pos) return;
    positions.set(id, { x: pos.x + dx, y: pos.y + dy });
  });
}

/**
 * Assign each fed-account a sequential segment index.
 * Seeds (no inbound exec bridge) = 0; hop targets = source segment + 1.
 * First assignment wins (revisits keep the earlier segment; soft edges cover later hops).
 */
export function assignSequentialAccountSegments(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  bridges: ExecutingAccountBridge[]
): Map<string, number> {
  const childrenByParent = buildChildrenMap(edges);
  const parentByChild = buildParentMap(edges);
  const accounts = nodes.filter((node) => node.type === "account");
  const accountIds = new Set(accounts.map((node) => node.id));

  const inbound = new Set(bridges.map((bridge) => bridge.toAccountNodeId));
  const seeds = accounts
    .filter((account) => !inbound.has(account.id))
    .sort((a, b) => a.id.localeCompare(b.id));

  const segmentByAccount = new Map<string, number>();
  const queue: string[] = [];

  for (const seed of seeds) {
    segmentByAccount.set(seed.id, 0);
    queue.push(seed.id);
  }

  // Bridges grouped by source policy for stable fan-out ordering
  const bridgesByPolicy = new Map<string, ExecutingAccountBridge[]>();
  for (const bridge of bridges) {
    const list = bridgesByPolicy.get(bridge.fromPolicyNodeId) ?? [];
    list.push(bridge);
    bridgesByPolicy.set(bridge.fromPolicyNodeId, list);
  }

  const policyAccountOwner = new Map<string, string>();
  for (const account of accounts) {
    Array.from(collectSubtreeIds(account.id, childrenByParent)).forEach((id) => {
      const node = nodes.find((n) => n.id === id);
      if (node && (node.type === "policy" || node.type === "combined_policy")) {
        policyAccountOwner.set(id, account.id);
      }
    });
  }

  let guard = 0;
  while (queue.length > 0 && guard < accounts.length * 4) {
    guard += 1;
    const accountId = queue.shift()!;
    const seg = segmentByAccount.get(accountId) ?? 0;
    const subtree = collectSubtreeIds(accountId, childrenByParent);

    Array.from(subtree).forEach((nodeId) => {
      const policyBridges = bridgesByPolicy.get(nodeId);
      if (!policyBridges) return;
      for (const bridge of policyBridges) {
        if (!accountIds.has(bridge.toAccountNodeId)) continue;
        if (segmentByAccount.has(bridge.toAccountNodeId)) continue;
        // Only hop from policies owned by this account lane
        if (policyAccountOwner.get(bridge.fromPolicyNodeId) !== accountId) continue;
        segmentByAccount.set(bridge.toAccountNodeId, seg + 1);
        queue.push(bridge.toAccountNodeId);
      }
    });
  }

  // Any account still unassigned (e.g. only reachable via odd edges) → residual seed band
  for (const account of accounts) {
    if (!segmentByAccount.has(account.id)) {
      segmentByAccount.set(account.id, 0);
    }
  }

  void parentByChild;
  return segmentByAccount;
}

function computeSegmentPitch(
  nodes: WorkspaceNode[],
  positions: Map<string, WorkspaceNodePosition>
): number {
  let minX = WORKSPACE_GRAPH_ORIGIN_X;
  let maxRight = WORKSPACE_GRAPH_ORIGIN_X;
  for (const node of nodes) {
    const pos = positions.get(node.id);
    if (!pos) continue;
    const { width } = getWorkspaceNodeDimensions(node);
    minX = Math.min(minX, pos.x);
    maxRight = Math.max(maxRight, pos.x + width);
  }
  const pipelineSpan = Math.max(maxRight - minX, WORKSPACE_GRAPH_COLUMN_GAP * 4);
  const execSlot =
    WORKSPACE_PLACEHOLDER_COLUMN_GAP +
    WORKSPACE_PLACEHOLDER_NODE_SIZE +
    WORKSPACE_PLACEHOLDER_COLUMN_GAP;
  return pipelineSpan + execSlot + WORKSPACE_GRAPH_COLUMN_GAP;
}

/**
 * Strategy Sequential layout: Parallel pack first, then shift each fed-account
 * lane right by its hop segment so executing-account bridges read as a story.
 */
export function layoutStrategyGraphSequential(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: { compact?: boolean }
): Map<string, WorkspaceNodePosition> {
  const compact = options?.compact ?? false;
  const positions = layoutWorkspaceGraphTree(nodes, edges, {
    viewMode: "strategy",
    compact,
  });
  if (nodes.length === 0) return positions;

  const bridges = listExecutingAccountBridges(nodes, edges);
  const segmentByAccount = assignSequentialAccountSegments(nodes, edges, bridges);
  const childrenByParent = buildChildrenMap(edges);
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const branchGap = layoutBranchGap(compact);
  const pitch = computeSegmentPitch(nodes, positions);

  const accounts = nodes
    .filter((node) => node.type === "account")
    .sort((a, b) => {
      const segA = segmentByAccount.get(a.id) ?? 0;
      const segB = segmentByAccount.get(b.id) ?? 0;
      if (segA !== segB) return segA - segB;
      return a.id.localeCompare(b.id);
    });

  // Horizontal shift by segment
  for (const account of accounts) {
    const segment = segmentByAccount.get(account.id) ?? 0;
    if (segment <= 0) continue;
    const dx = segment * pitch;
    shiftSubtree(account.id, dx, 0, childrenByParent, positions);
  }

  // Vertical pack within each segment so fan-out / residual lanes don't overlap
  const bySegment = new Map<number, WorkspaceNode[]>();
  for (const account of accounts) {
    const segment = segmentByAccount.get(account.id) ?? 0;
    const list = bySegment.get(segment) ?? [];
    list.push(account);
    bySegment.set(segment, list);
  }

  for (const [, laneAccounts] of Array.from(bySegment.entries()).sort(
    (a, b) => a[0] - b[0]
  )) {
    let yCursor = Math.min(
      ...laneAccounts.map((account) => {
        const extent = measureSubtreeExtent(
          account.id,
          positions,
          childrenByParent,
          nodeById
        );
        return extent?.top ?? positions.get(account.id)?.y ?? 0;
      })
    );

    for (const account of laneAccounts) {
      const extent = measureSubtreeExtent(
        account.id,
        positions,
        childrenByParent,
        nodeById
      );
      if (!extent) continue;
      const dy = yCursor - extent.top;
      shiftSubtree(account.id, 0, dy, childrenByParent, positions);
      const packed = measureSubtreeExtent(
        account.id,
        positions,
        childrenByParent,
        nodeById
      );
      yCursor = (packed?.bottom ?? extent.bottom + dy) + branchGap;
    }
  }

  // Keep strategy root near segment-0 account diamonds
  const strategy = nodes.find((node) => node.type === "strategy");
  if (strategy) {
    const seedAccounts = accounts.filter(
      (account) => (segmentByAccount.get(account.id) ?? 0) === 0
    );
    const centers: number[] = [];
    for (const account of seedAccounts) {
      const pos = positions.get(account.id);
      if (!pos) continue;
      const { height } = getWorkspaceNodeDimensions(account);
      centers.push(pos.y + height / 2);
    }
    const strategyPos = positions.get(strategy.id);
    if (strategyPos && centers.length > 0) {
      const { height } = getWorkspaceNodeDimensions(strategy);
      const targetCenter = centers.reduce((sum, y) => sum + y, 0) / centers.length;
      positions.set(strategy.id, {
        x: strategyPos.x,
        y: targetCenter - height / 2,
      });
    }
  }

  return positions;
}

/** Unified entry used by Organize / hide packing. */
export function layoutWorkspaceGraph(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    viewMode?: "account" | "strategy";
    compact?: boolean;
    layoutMode?: WorkspaceLayoutMode;
    maxSimulationsPerReport?: number;
  }
): Map<string, WorkspaceNodePosition> {
  const viewMode = options?.viewMode ?? "account";
  const layoutMode = options?.layoutMode ?? "parallel";
  if (viewMode === "strategy" && layoutMode === "sequential") {
    return layoutStrategyGraphSequential(nodes, edges, {
      compact: options?.compact,
    });
  }
  return layoutWorkspaceGraphTree(nodes, edges, {
    viewMode,
    compact: options?.compact,
    maxSimulationsPerReport: options?.maxSimulationsPerReport,
  });
}
